import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Attention, Button, Icon, Textarea, ToggleButton } from "@optimizely/axiom";
import { useInstancePermissions } from "../../../hooks/useInstancePermissions/useInstancePermissions";
import { isEmail } from "../../../lib/validators";
import { getErrorMessage } from "../../../lib/utils";
import { emitToast } from "../../../lib/toaster-utils";
import { LoadingIndicator } from "../LoadingIndicator/LoadingIndicator";
import { IUserInstancePermission } from "../../../domain/UserInstancePermission";
import { UserPermissionsDisplay } from "./UserPermissionsDisplay";
import { datadogRum } from "@datadog/browser-rum";

export const UserPermissionsForm = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const {
        register,
        getValues,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<{ email: string }>({
        mode: "onChange"
    });
    const [permissions, setPermissions] = useState<IUserInstancePermission[]>([]);
    const { getUserInstancePermissions } = useInstancePermissions();
    const [displayMode, setDisplayMode] = useState<"json" | "table">("table");
    const onSubmit = ({ email }: { email: string }) => {
        setLoading(true);
        setError("");
        setPermissions([]);
        getUserInstancePermissions({ email })
            .then((res) => {
                setPermissions(res);
            })
            .catch((err) => {
                datadogRum.addError(new Error("Support: Failed to get user instance permissions."), { error: err });
                setError(getErrorMessage(err));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const copyToClipboard = () => {
        if (!navigator.clipboard) {
            emitToast({ message: "Clipboard API not available.", type: "bad-news" });
            return;
        }
        const value = JSON.stringify(permissions, null, 2);
        navigator.clipboard
            .writeText(value)
            .then(() => {
                emitToast({ message: "Copied!" });
            })
            .catch(() => {
                emitToast({ message: "Failed to copy to clipboard.", type: "bad-news" });
            });
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedText = event.clipboardData.getData("text");
        setValue("email", pastedText, { shouldValidate: true, shouldDirty: true });
        event.preventDefault();
    };

    const hasData = permissions.length > 0;
    return (
        <>
            <form className="flex flex--column user-form" onSubmit={handleSubmit(onSubmit)}>
                <div className="oui-dropdown-group push-double--ends min-width--250 width--7-12">
                    <input
                        data-cy="user-email-input"
                        placeholder="User email..."
                        aria-describedby="user-email-error"
                        className="oui-text-input"
                        id="user-email"
                        disabled={loading}
                        type="email"
                        {...register("email", {
                            validate: { isEmail },
                            required: { value: true, message: "Email is required" }
                        })}
                        onPaste={handlePaste}
                    />
                    {errors.email && (
                        <div className="oui-form-bad-news">
                            <span className="oui-form-note">{errors.email.message}</span>
                        </div>
                    )}
                </div>
                <Button
                    style="highlight"
                    isDisabled={loading || !getValues("email")}
                    className="max-width--200 push-triple--bottom"
                    isSubmit
                >
                    Get User Permissions
                </Button>
            </form>
            {error && <Attention type="bad-news">{error}</Attention>}
            {loading && <LoadingIndicator type="spinner" height="120px" width="120px" />}
            {hasData && !loading && (
                <>
                    <div className="flex justify-content-end">
                        <ToggleButton.Group
                            value={displayMode}
                            onChange={(val) => setDisplayMode(val as "json" | "table")}
                        >
                            <ToggleButton
                                data-cy="table-view-toggle"
                                value="table"
                                // @ts-expect-error we don't import icon library so need to use the component here
                                icon={<Icon name="table-cells" size="small" />}
                                toolTipText="View As Table"
                                toolTipPosition="bottom"
                            />
                            <ToggleButton
                                data-cy="json-view-toggle"
                                toolTipPosition="bottom"
                                value="json"
                                // @ts-expect-error we don't import icon library so need to use the component here
                                icon={<Icon name="brackets-curly-thin" size="small" />}
                                toolTipText="View Raw JSON"
                            />
                        </ToggleButton.Group>
                    </div>
                    {displayMode === "json" && (
                        <Textarea
                            isDisabled
                            value={JSON.stringify(permissions, null, 2)}
                            label="User Permissions"
                            aria-label="User permissions data in JSON format"
                            rows={12}
                        />
                    )}
                    {displayMode === "table" && <UserPermissionsDisplay permissions={permissions} />}
                    <Button
                        data-cy="copy-permissions-button"
                        style="highlight"
                        className="push-double--top"
                        onClick={copyToClipboard}
                    >
                        <Icon size="small" name="copy" />
                        Copy to Clipboard
                    </Button>
                </>
            )}
        </>
    );
};

UserPermissionsForm.displayName = "UserPermissionsForm";
